/**
 * Mark Elements as "ready"
 * @module framework/helpers
 */

/**
 * Ready attribute name
 */
export const DATA_READY = 'data-ready';

/**
 * Sets `data-ready` attribute on the provided element
 */
export function markElementReady(element: HTMLElement): void {
    element.dataset.ready = 'ready';
}

/**
 * Sets 'data-ready' attribute on the provided elements
 */
export function markElementsReady(elements: NodeListOf<HTMLElement>): void {
    for (const element of elements) {
        markElementReady(element);
    }
}

/**
 * Check if an element has been marked as ready
 */
export function isElementReady(element: HTMLElement): boolean {
    return element.hasAttribute(DATA_READY);
}

/**
 * Resolves when an element is marked as 'ready'.
 * If the element is already 'ready', resolves immediately.
 */
export async function elementReady(element: HTMLElement): Promise<void> {
    if (isElementReady(element)) return;

    return new Promise<void>((resolve) => {
        const observer = new MutationObserver(() => {
            observer.disconnect();
            resolve();
        });

        observer.observe(element, {
            attributes: true,
            attributeFilter: [DATA_READY],
        });
    });
}

/* A more generic function to elementReady above */
export async function waitForElement(selector: string, parentNode: HTMLElement | Document = document) {
    return new Promise(resolve => {
        if (parentNode.querySelector(selector)) {
            return resolve(parentNode.querySelector(selector));
        }

        const observer = new MutationObserver(mutations => {
            if (parentNode.querySelector(selector)) {
                resolve(parentNode.querySelector(selector));
                observer.disconnect();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
}

